import React, { useState, useRef, useEffect } from 'react';
import { useCandidateProfiles } from '@/hooks/useCandidate';
import { useToggleSaveListing } from '@/hooks/useSavedListings';
import { Search, MapPin, Briefcase, DollarSign, BookOpen, X, ChevronDown, Check, Heart, FileEdit } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Pagination, Spinner, EmptyState, } from '@/components/common';
import { useProvinces, useDistricts } from '@/hooks/useAddress';
import { cn, formatSalaryRange } from '@/lib/utils';
import dayjs from '@/lib/dayjs';
import type { CandidateFilter } from '@/types/candidates/candidates.types';
import { CandidateAdvancedFilter } from '@/components/jobs/CandidateAdvancedFilter';
import { CandidateTable } from '@/components/jobs/CandidateTable';
import { CandidateDetailModal } from '@/components/jobs/CandidateDetailModal';
import { RecruiterNoteModal } from '@/components/jobs/RecruiterNoteModal';
import { Menu, MenuItem, ListItemIcon } from '@mui/material';
const SALARY_RANGES = [
  { label: 'Tất cả', min: null, max: null },
  { label: 'Dưới 10 triệu', min: 0, max: 10000000 },
  { label: '10 - 15 triệu', min: 10000000, max: 15000000 },
  { label: '15 - 20 triệu', min: 15000000, max: 20000000 },
  { label: '20 - 25 triệu', min: 20000000, max: 25000000 },
  { label: '25 - 30 triệu', min: 25000000, max: 30000000 },
  { label: '30 - 50 triệu', min: 30000000, max: 50000000 },
  { label: 'Trên 50 triệu', min: 50000000, max: null },
];

const EXPERIENCE_RANGES = [
  { label: 'Tất cả', min: null, max: null },
  { label: 'Chưa có kinh nghiệm', min: 0, max: 0 },
  { label: 'Dưới 1 năm', min: 0, max: 1 },
  { label: '1 - 2 năm', min: 1, max: 2 },
  { label: '2 - 3 năm', min: 2, max: 3 },
  { label: '3 - 5 năm', min: 3, max: 5 },
  { label: 'Trên 5 năm', min: 5, max: null },
];

export default function SearchCandidatesPage() {
  const navigate = useNavigate();
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionCandidateId, setActionCandidateId] = useState<string | null>(null);
  const [noteCandidate, setNoteCandidate] = useState<any>(null);

  const { toggleSave, isSaved } = useToggleSaveListing();
  const isCandidateSaved = actionCandidateId ? isSaved(actionCandidateId) : false;

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, candidateId: string) => {
    setAnchorEl(event.currentTarget);
    setActionCandidateId(candidateId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActionCandidateId(null);
  };

  const handleSaveCandidate = () => {
    if (actionCandidateId) {
      toggleSave(actionCandidateId, 'JobOfferRequest');
    }
    handleCloseMenu();
  };

  const handleOpenNoteModal = () => {
    if (actionCandidateId) {
      const candidate = mappedCandidates.find(c => c.id === actionCandidateId);
      if (candidate) {
        setNoteCandidate(candidate);
      }
    }
    handleCloseMenu();
  };
  const [filters, setFilters] = useState<CandidateFilter>({ page: 1, limit: 12 });
  const { data, isLoading, isFetching } = useCandidateProfiles(filters);
  const rawCandidates = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / (filters.limit || 12));

  const handleFilterChange = (newFilters: Partial<CandidateFilter>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const mappedCandidates = rawCandidates.map(c => {
    return {
      id: c.id,
      isReal: true,
      fullName: c.fullName || 'Chưa cập nhật',
      email: c.email || 'Chưa cập nhật',
      phoneNumber: c.phoneNumber || '',
      position: c.desiredPosition || 'Chưa cập nhật vị trí',
      categoryAndType: `${c.jobCategoryName || ''} ${c.jobCategoryName && c.jobType ? '•' : ''} ${c.jobType || ''}`.trim() || 'Chưa cập nhật',
      experience: c.experience ? `${c.experience} năm` : 'Chưa cập nhật KN',
      education: c.education || 'Chưa cập nhật HV',
      salary: formatSalaryRange(c.expectedSalaryMin, c.expectedSalaryMax),
      location: c.province?.name || c.location || 'Chưa cập nhật',
      status: 'screening',
      statusLabel: 'Chưa ứng tuyển',
      date: dayjs(c.createdAt).format('DD/MM/YYYY'),
      avatar: c.images?.find((img: any) => img.isPrimary)?.image?.publicUrl || null,
      recruiterNote: c.recruiterNote?.notes || '',
      rawProfile: c
    };
  });

  return (
    <div className="w-full">
      <CandidateAdvancedFilter onFilter={handleFilterChange} className="mb-8" />

      {/* ── Main Content Grid ── */}
      <div className="relative min-h-[400px]">
        {(isLoading || isFetching) && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-2xl">
            <Spinner />
          </div>
        )}

        {!isLoading && mappedCandidates.length === 0 ? (
          <EmptyState title="Không tìm thấy ứng viên nào" description="Thử thay đổi bộ lọc tìm kiếm của bạn" icon="🧑‍💼" />
        ) : (
          <>
            <CandidateTable
              candidates={mappedCandidates}
              onViewDetail={(c) => setSelectedCandidate(c)}
              onActionClick={handleOpenMenu}
              pageSize={filters.limit || 12}
              setPageSize={(size) => setFilters(prev => ({ ...prev, limit: size, page: 1 }))}
              currentPage={filters.page || 1}
              setCurrentPage={(p) => setFilters(prev => ({ ...prev, page: p }))}
              totalPages={totalPages}
            />

            <CandidateDetailModal
              open={!!selectedCandidate}
              onClose={() => setSelectedCandidate(null)}
              candidate={selectedCandidate}
              onAccept={(id) => {
                toggleSave(id, 'JobOfferRequest');
              }}
            />

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              sx={{
                '& .MuiPaper-root': {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 4px 20px rgba(0,0,0,0.08))',
                  mt: 1,
                  borderRadius: '16px',
                  border: '1px solid #f1f3f5',
                  minWidth: '160px'
                }
              }}
            >
              <MenuItem onClick={handleSaveCandidate} sx={{ fontSize: '13px', fontWeight: 'bold', color: isCandidateSaved ? '#ef4444' : '#64748b', py: 1.5, '&:hover': { color: isCandidateSaved ? '#ef4444' : '#f97316', backgroundColor: isCandidateSaved ? '#fef2f2' : '#fff2eb' } }}>
                <ListItemIcon>
                  <Heart size={16} className={isCandidateSaved ? "text-[#ef4444] fill-[#ef4444]" : "text-inherit"} />
                </ListItemIcon>
                {isCandidateSaved ? 'Bỏ lưu ứng viên' : 'Lưu ứng viên'}
              </MenuItem>
              <MenuItem onClick={handleOpenNoteModal} sx={{ fontSize: '13px', fontWeight: 'bold', color: '#64748b', py: 1.5, '&:hover': { color: '#f97316', backgroundColor: '#fff2eb' } }}>
                <ListItemIcon>
                  <FileEdit size={16} className="text-inherit" />
                </ListItemIcon>
                Thêm ghi chú
              </MenuItem>
            </Menu>

            {noteCandidate && (
              <RecruiterNoteModal
                open={!!noteCandidate}
                onClose={() => setNoteCandidate(null)}
                candidateProfileId={noteCandidate.id}
                candidateName={noteCandidate.fullName}
                existingNote={noteCandidate.rawProfile?.recruiterNote}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
